import { CSSProperties, ReactElement, useEffect, useState } from "react";
import { IChargeWeight, IPanelProps, IProductionResults } from "./types";
import { cloneObject } from "../../../../system/utils";
import RowComponent from "../../components/rowComponent";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CachedIcon from "@mui/icons-material/Cached";
import { Checkbox, Paper } from "@mui/material";
import { IShiftHistory } from "../../../../types";
import { useAsyncFetch } from "../../../../system/useFetch";
import InputAdornment from "@mui/material/InputAdornment";
import DeleteIcon from "@mui/icons-material/Delete";

const heatGridStyle = {
  gridTemplateColumns: "1fr 1fr"
};

const titleCellStyle = (): CSSProperties => ({
  padding: ".5em",
  alignSelf: "stretch",
  gridColumn: "1 / span 1",
  borderRadius: 5,
  background: "rgba(50, 50, 50, .3)",
  width: 150
});

interface IProps extends IPanelProps<IProductionResults> {
  shiftHistory: IShiftHistory;
}

interface IHeatBase {
  heatNumber: number;
  weight: number | null;
  powerON: number | null;
  powerOFF: number | null;
}

const ProductionPanel = ({ value, property, onChange, shiftHistory }: IProps): ReactElement => {
  const [ checked, setChecked ] = useState<number[]>([]);
  const [ dispatchAPI ] = useAsyncFetch<IHeatBase[]>();

  useEffect(() => {
    if (!value.vcbTrips) {
      const newObj = cloneObject(value);
      newObj.vcbTrips = { value: 0, comment: "" };

      onChange("production", newObj);
    }
  }, [value]);

  const onChecked = (index: number, isChecked: boolean) => {
    if (isChecked) {
      setChecked([...checked, index]);
    } else {
      setChecked(checked.filter(x => x !== index));
    }
  };

  const onRefreshHeats = () => {
    dispatchAPI({
      module: "eafHeat",
      query: "getHeatsBaseByShift",
      payload: {
        shiftHistoryID: shiftHistory.shiftHistoryID
      }
    }).then(heats => {
      if (heats) {
        const newObj = cloneObject(value);
        let powerON = 0;
        let powerOFF = 0;
        let totalWeight = 0;

        heats.forEach(hA => {
          const hB = newObj.chargeWeight.find(h => h.heat === hA.heatNumber);

          powerON += hA.powerON ?? 0;
          powerOFF += hA.powerOFF ?? 0;
          totalWeight += hA.weight ?? 0;

          if (hB) {
            hB.weight = Number(hB.weight) === 0 ?  Math.round(hA.weight ?? 0) : hB.weight;
          } else {
            newObj.chargeWeight.push({ heat: hA.heatNumber, weight: Math.round(hA.weight ?? 0) });
          }
        });

        newObj.chargeWeight = newObj.chargeWeight.sort((a, b) => a.heat - b.heat);
        newObj.powerOn = Number(newObj.powerOn.value) === 0 ? { ...newObj.powerOn, value: Math.trunc(powerON * 10) / 10 } : newObj.powerOn;
        newObj.powerOff = Number(newObj.powerOff.value) === 0 ? { ...newObj.powerOff, value: Math.trunc(powerOFF * 10) / 10 } : newObj.powerOff;
        newObj.totalProduction = Number(newObj.totalProduction.value) === 0 ? { ...newObj.totalProduction, value: totalWeight } : newObj.totalProduction;

        onChange("production", newObj);
        setChecked([]);
      }
    });
  };

  const onAddNewHeat = () => {
    const newObj = cloneObject(value);
    newObj.chargeWeight.push({ heat: 0, weight: 0 });

    onChange("production", newObj);
    setChecked([]);
  };

  const onRemoveSelectedHeats = () => {
    const newObj = cloneObject(value);
    newObj.chargeWeight = value.chargeWeight.filter(h => !checked.includes(h.heat));

    onChange("production", newObj);
    setChecked([]);
  };

  const onChangeData = (prop: keyof IProductionResults, subProp: string, newValue: any) => {
    const newObj = cloneObject(value);

    if (["heatsDuringShift", "powerOn", "powerOff", "totalProduction", "missedHeats", "metallicYieldLoss", "vcbTrips"].includes(prop)) {
      (newObj[prop] as any)[subProp] = newValue;
    }

    onChange(property, newObj);
  };

  const onChangeHeat = (index: number, prop: keyof IChargeWeight, newValue: any) => {
    const newObj = cloneObject(value);

    newObj.chargeWeight[index][prop] = newValue;
    onChange(property, newObj);
  };

  return (
    <div className="display-flex-column gap-1 p-1">
      <RowComponent title="Update" ratio={.2} >
        <div>
          <IconButton size={"small"} color="info" onClick={onRefreshHeats}>
            <CachedIcon/>
          </IconButton>
        </div>
      </RowComponent>
      <RowComponent title="Heats during shift" ratio={.2} >
        <div className="row-content-2">
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            type="number"
            value = {value.heatsDuringShift.value}
            fullWidth={true}
            onChange={event => onChangeData("heatsDuringShift", "value", event.target.value)}
          />
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            multiline={true}
            minRows={4}
            maxRows={4}
            value = {value.heatsDuringShift.comment}
            fullWidth={true}
            onChange={event => onChangeData("heatsDuringShift", "comment", event.target.value)}
          />
        </div>
      </RowComponent>
      <RowComponent title="Power On" ratio={.2} >
        <div className="row-content-2">
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            type="number"
            value = {value.powerOn.value}
            fullWidth={true}
            onChange={event => onChangeData("powerOn", "value", event.target.value)}
          />
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            multiline={true}
            minRows={4}
            maxRows={4}
            value = {value.powerOn.comment}
            fullWidth={true}
            onChange={event => onChangeData("powerOn", "comment", event.target.value)}
          />
        </div>
      </RowComponent>
      <RowComponent title="Power Off" ratio={.2} >
        <div className="row-content-2">
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            type="number"
            value = {value.powerOff.value}
            fullWidth={true}
            onChange={event => onChangeData("powerOff", "value", event.target.value)}
          />
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            multiline={true}
            minRows={4}
            maxRows={4}
            value = {value.powerOff.comment}
            fullWidth={true}
            onChange={event => onChangeData("powerOff", "comment", event.target.value)}
          />
        </div>
      </RowComponent>
      <RowComponent title="Total Production" ratio={.2} >
        <div className="row-content-2">
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            type="number"
            value = {value.totalProduction.value}
            fullWidth={true}
            onChange={event => onChangeData("totalProduction", "value", event.target.value)}
          />
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            multiline={true}
            minRows={4}
            maxRows={4}
            value = {value.totalProduction.comment}
            fullWidth={true}
            onChange={event => onChangeData("totalProduction", "comment", event.target.value)}
          />
        </div>
      </RowComponent>

      <RowComponent title="Charge Weight" ratio={.2} >
        <div className="display-flex-column gap-1">
          <div className="display-flex-row gap-1" style={heatGridStyle}>
            <Paper elevation={0}  style={titleCellStyle()} className="display-grid">
              <div className="align-self-center justify-self-center display-grid">
                <strong >Heat #</strong>
              </div>
            </Paper>
            <Paper elevation={0}  style={titleCellStyle()} className="display-grid">
              <div className="align-self-center justify-self-center display-flex-column">
                <strong >Weight</strong>
              </div>
            </Paper>

            <div className="flex-grow-1 gap-1">
              <IconButton size={"small"}  color="success" onClick={onAddNewHeat}>
                <AddCircleOutlineIcon/>
              </IconButton>
              <IconButton size={"small"}  color="error" onClick={onRemoveSelectedHeats} disabled={checked.length === 0}>
                <DeleteIcon/>
              </IconButton>
            </div>
          </div>
          { value.chargeWeight.map((heat, index) => {

            return (
              <div key={`chargeWeight-${index}`} className="display-flex-row gap-1" style={heatGridStyle}>
                <TextField
                  variant="outlined"
                  placeholder="to be filled..."
                  type="number"
                  value = {heat.heat}
                  fullWidth={true}
                  style={{ width: 150 }}
                  slotProps={{ input: {
                      startAdornment: <InputAdornment position="start">
                      <Checkbox
                        style={{ width: 30 }}
                        checked={checked.includes(heat.heat)}
                        tabIndex={-1}
                        disableRipple={true} onChange={(_event, isChecked) => onChecked(heat.heat, isChecked)}/>
                    </InputAdornment>,
                  }}}
                  onChange={event => onChangeHeat(index, "heat", event.target.value)}
                />
                <TextField
                  variant="outlined"
                  placeholder="to be filled..."
                  type="number"
                  value = {heat.weight}
                  fullWidth={true}
                  style={{ width: 150 }}
                  onChange={event => onChangeHeat(index, "weight", event.target.value)}
                />
              </div>
            );
          })}
        </div>
      </RowComponent>

      <RowComponent title="Missed heats" ratio={.2} >
        <div className="row-content-2">
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            type="number"
            value = {value.missedHeats.value}
            fullWidth={true}
            onChange={event => onChangeData("missedHeats", "value", event.target.value)}
          />
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            multiline={true}
            minRows={4}
            maxRows={4}
            value = {value.missedHeats.comment}
            fullWidth={true}
            onChange={event => onChangeData("missedHeats", "comment", event.target.value)}
          />
        </div>
      </RowComponent>
      <RowComponent title="Metallic yield loss" ratio={.2} >
        <div className="row-content-2 align-content-stretch" >
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            type="number"
            value = {value.metallicYieldLoss.value}
            fullWidth={true}
            onChange={event => onChangeData("metallicYieldLoss", "value", event.target.value)}
          />
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            multiline={true}
            minRows={4}
            maxRows={4}
            value = {value.metallicYieldLoss.comment}
            fullWidth={true}
            onChange={event => onChangeData("metallicYieldLoss", "comment", event.target.value)}
          />
        </div>
      </RowComponent>
      {value.vcbTrips && <RowComponent title="VCB Trips" ratio={.2}>
        <div className="row-content-2 align-content-stretch">
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            type="number"
            value={value.vcbTrips.value ?? 0}
            fullWidth={true}
            onChange={event => onChangeData("vcbTrips", "value", event.target.value)}
          />
          <TextField
            variant="outlined"
            placeholder="to be filled..."
            multiline={true}
            minRows={4}
            maxRows={4}
            value={value.vcbTrips.comment ?? ""}
            fullWidth={true}
            onChange={event => onChangeData("vcbTrips", "comment", event.target.value)}
          />
        </div>
      </RowComponent>}
    </div>
  );
};

export default ProductionPanel;